/**
 * 30-Day Unlock Report — document view fed by /api/reports/30-day
 */

import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import Navigation from '@/components/navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Droplets, TrendingUp, Calendar, Trophy, ArrowLeft, Printer } from 'lucide-react';
import type { ThirtyDayReportMetrics } from '@shared/thirtyDayReport';

type ReportResponse = ThirtyDayReportMetrics & { message?: string };

function mlToL(ml: number): string {
  return `${(ml / 1000).toFixed(1)}L`;
}

export default function UnlockReport() {
  const [, setLocation] = useLocation();
  const { data, isLoading, error } = useQuery<ReportResponse>({
    queryKey: ['/api/reports/30-day'],
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-hydro-100 flex items-center justify-center pb-24">
        <p className="text-deep-600 font-medium">Building your report…</p>
        <Navigation />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-hydro-100 px-5 py-8 pb-24 max-w-md mx-auto">
        <button
          type="button"
          onClick={() => setLocation('/profile')}
          className="flex items-center gap-2 text-deep-600 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Card className="p-6 text-center">
          <p className="text-deep-700">Could not load your report. Try again later.</p>
        </Card>
        <Navigation />
      </div>
    );
  }

  if (!data.eligible) {
    return (
      <div className="min-h-screen bg-hydro-100 px-5 py-8 pb-24 max-w-md mx-auto">
        <button
          type="button"
          onClick={() => setLocation('/profile')}
          className="flex items-center gap-2 text-deep-600 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Card className="p-8 text-center space-y-4 border-lilac-300">
          <Lock className="w-10 h-10 mx-auto text-purple-600" />
          <h1 className="text-xl font-bold text-deep-800">30-Day Unlock Report</h1>
          <p className="text-sm text-deep-600 leading-relaxed">
            After 30 days on SWYG you get a dedicated Hydration Report here (trends, badges, and a
            printable summary). This is separate from day-to-day Analytics.
          </p>
          <p className="text-sm text-deep-600 leading-relaxed">{data.message}</p>
          <p className="text-xs text-deep-500">
            Day {data.daysSinceSignup} of {data.unlockDaysRequired}
          </p>
          <Button onClick={() => setLocation('/analytics')} className="rounded-full">
            View Analytics meanwhile
          </Button>
        </Card>
        <Navigation />
      </div>
    );
  }

  const topFrequency =
    Object.entries(data.frequencyBadgeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
  const topVolume =
    Object.entries(data.volumeBadgeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

  return (
    <div className="min-h-screen bg-hydro-100 pb-24 print:bg-white print:pb-0">
      <div className="max-w-md mx-auto px-5 py-6 space-y-4 print:max-w-none">
        <div className="flex items-center justify-between gap-3 print:hidden">
          <button
            type="button"
            onClick={() => setLocation('/profile')}
            className="flex items-center gap-2 text-deep-600 shrink-0"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <Button
            type="button"
            variant="outline"
            className="rounded-full shrink-0"
            onClick={() => window.print()}
          >
            <Printer className="w-4 h-4 mr-2" />
            Save / Print
          </Button>
        </div>

        <Card className="p-5 bg-cream border-lilac-300 overflow-hidden">
          <div className="flex items-start gap-3 mb-4 min-w-0">
            <Droplets className="w-6 h-6 text-purple-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-deep-800 leading-tight break-words">
                Your 30-Day Hydration Journey
              </h1>
              <p className="text-xs text-deep-500 mt-1 leading-relaxed">
                This is your Unlock Report — a printable 30-day summary (not Analytics). Email and
                Profile CTAs open this page at <span className="font-semibold">/unlock-report</span>
                .
              </p>
              <p className="text-xs text-deep-500 mt-1 break-words">
                {data.userName}
                {data.age != null ? ` · Age ${data.age}` : ''} · {data.usageDays} days on SWYG
              </p>
              <p className="text-xs text-deep-400 mt-0.5 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {data.periodStart} → {data.periodEnd}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="bg-purple-600 text-white rounded-xl p-3 text-center min-w-0">
              <p className="text-[10px] opacity-80">Before</p>
              <p className="text-lg font-bold truncate">{mlToL(data.beforeAvgMl)}</p>
              <p className="text-[10px] opacity-70">avg daily</p>
            </div>
            <div className="bg-lilac-400 text-deep-800 rounded-xl p-3 text-center min-w-0">
              <p className="text-[10px] opacity-80">After</p>
              <p className="text-lg font-bold truncate">{mlToL(data.afterAvgMl)}</p>
              <p className="text-[10px] opacity-70">avg daily</p>
            </div>
            <div className="bg-lime-400 text-deep-800 rounded-xl p-3 text-center min-w-0">
              <p className="text-[10px] opacity-80 flex items-center justify-center gap-1">
                <TrendingUp className="w-3 h-3" /> Change
              </p>
              <p className="text-lg font-bold">
                {data.improvementPercent >= 0 ? '+' : ''}
                {data.improvementPercent}%
              </p>
              <p className="text-[10px] opacity-70">vs baseline</p>
            </div>
            <div className="bg-deep-800 text-white rounded-xl p-3 text-center min-w-0">
              <p className="text-[10px] opacity-80 flex items-center justify-center gap-1">
                <Trophy className="w-3 h-3" /> Rank
              </p>
              <p className="text-lg font-bold truncate">
                {data.companyRank != null ? `#${data.companyRank}` : '—'}
              </p>
              <p className="text-[10px] opacity-70">
                {data.companySize != null ? `of ${data.companySize}` : 'company'}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-cream border-deep-100">
          <h2 className="font-bold text-deep-800 mb-3">Usage snapshot</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-deep-500">Days with logs</span>
              <span className="font-semibold text-deep-800">{data.daysWithLogs}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-deep-500">Avg daily intake</span>
              <span className="font-semibold text-deep-800">{mlToL(data.avgDailyMl)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-deep-500">Total volume</span>
              <span className="font-semibold text-deep-800">{mlToL(data.totalVolumeMl)}</span>
            </div>
            <div className="flex justify-between gap-3 min-w-0">
              <span className="text-deep-500 shrink-0">Top frequency badge</span>
              <span className="font-semibold text-deep-800 truncate">{topFrequency}</span>
            </div>
            <div className="flex justify-between gap-3 min-w-0">
              <span className="text-deep-500 shrink-0">Top volume badge</span>
              <span className="font-semibold text-deep-800 truncate">{topVolume}</span>
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-cream border-deep-100">
          <h2 className="font-bold text-deep-800 mb-3">Weekly volume (avg L)</h2>
          <div className="grid grid-cols-4 gap-2">
            {data.weeklyVolumeL.map((w) => (
              <div key={w.week} className="bg-lilac-100 rounded-xl p-2 text-center min-w-0">
                <p className="text-[10px] text-deep-500 truncate">{w.week}</p>
                <p className="font-bold text-deep-800">{w.volume}</p>
              </div>
            ))}
          </div>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4 bg-cream border-deep-100 overflow-hidden">
            <h3 className="text-xs font-bold text-deep-800 mb-2">Best days</h3>
            <ul className="space-y-2">
              {data.bestDays.length === 0 && <li className="text-xs text-deep-500">No logs yet</li>}
              {data.bestDays.map((d) => (
                <li key={d.date} className="text-xs text-deep-700 break-words">
                  {d.date}
                  <span className="block font-bold">{mlToL(d.valueMl)}</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="p-4 bg-cream border-deep-100 overflow-hidden">
            <h3 className="text-xs font-bold text-deep-800 mb-2">Days to improve</h3>
            <ul className="space-y-2">
              {data.daysToImprove.length === 0 && (
                <li className="text-xs text-deep-500">No logs yet</li>
              )}
              {data.daysToImprove.map((d) => (
                <li key={d.date} className="text-xs text-deep-700 break-words">
                  {d.date}
                  <span className="block font-bold">{mlToL(d.valueMl)}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
      <div className="print:hidden">
        <Navigation />
      </div>
    </div>
  );
}
